/**
 * Mobile navigation: a bottom bar plus a drawer for the overflow.
 *
 * He reads this on a phone, so the bar sits at the *bottom* — within thumb
 * reach — and respects `env(safe-area-inset-bottom)` so iOS's home
 * indicator does not sit on top of the tap targets. Five slots, 44px tall:
 * the two remaining destinations live in the drawer.
 */

import { X } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { useProtectionTruth } from '@/components/monitoring/useProtectionTruth';
import { cn } from '@/lib/cn';

import { Badge } from '../ui/Badge';
import { MOBILE_NAV_ITEMS, SHOWN_NAV_ITEMS } from './navigation';

export function MobileNav() {
  // The one protection truth [mv2-D2]: `?` when unknown, `…` while checking,
  // the count when above 0 — checking and unknown are never a silent zero.
  const protection = useProtectionTruth();
  const unprotectedBadge = protection.state === 'unknown' ? '?'
    : protection.state === 'checking' ? '…'
      : (protection.count ?? 0) > 0 ? String(protection.count) : null;
  const unprotectedLabel = protection.state === 'unknown' ? 'protection state unknown'
    : protection.state === 'checking' ? 'checking protection'
      : `${protection.count ?? 0} unprotected positions`;

  return (
    <nav
      aria-label="Primary (mobile)"
      className="flex h-14 shrink-0 items-stretch border-t border-border bg-surface-1 md:hidden"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      {MOBILE_NAV_ITEMS.map((item) => {
        const Icon = item.icon;
        // The live view carries the unprotected badge: if anything is
        // exposed, that has to be visible from every screen.
        const badge = item.to === '/monitoring' ? unprotectedBadge : null;
        return (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/'}
            className={({ isActive }) =>
              cn(
                'relative flex flex-1 flex-col items-center justify-center gap-0.5 transition-colors',
                isActive ? 'text-accent' : 'text-lo',
              )
            }
          >
            {({ isActive }) => (
              <>
                {isActive && (
                  <span
                    aria-hidden
                    className="absolute inset-x-3 top-0 h-0.5 rounded-b bg-accent"
                  />
                )}
                <span className="relative">
                  <Icon size={19} aria-hidden />
                  {badge !== null && (
                    <span
                      className={cn(
                        'num absolute -top-1 -right-2 flex h-3.5 min-w-3.5 items-center justify-center rounded-full px-1 text-[9px] font-bold',
                        protection.state === 'checking' ? 'bg-surface-3 text-lo'
                          : protection.state === 'unknown' && (protection.lastKnownCount ?? 0) === 0 ? 'bg-warn text-warn-fg'
                            : 'bg-danger text-danger-fg',
                      )}
                      aria-label={unprotectedLabel}
                      data-mobile-protection-badge={badge}
                    >
                      {badge}
                    </span>
                  )}
                </span>
                <span className="text-[10px] font-medium">{item.shortLabel}</span>
              </>
            )}
          </NavLink>
        );
      })}
    </nav>
  );
}

export interface MobileDrawerProps {
  open: boolean;
  onClose: () => void;
}

/** Full navigation list, for the destinations the bottom bar cannot hold. */
export function MobileDrawer({ open, onClose }: MobileDrawerProps) {
  const location = useLocation();

  // Navigating closes the drawer. Without this, tapping a link leaves the
  // overlay covering the page you just asked for.
  //
  // `onClose` goes through a ref so the effect depends on the pathname
  // alone: a parent that recreates the callback each render would otherwise
  // slam the drawer shut the moment it opened.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    closeRef.current();
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-40 bg-scrim md:hidden"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <aside
        className="slide-in flex h-full w-[248px] flex-col border-r border-border bg-surface-1"
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        <div className="flex h-11 shrink-0 items-center justify-between border-b border-border px-3">
          <span className="text-[13px] font-bold tracking-tight text-hi">
            Bheema<span className="text-accent">-PM</span>
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close navigation"
            className="rounded p-1 text-lo hover:bg-surface-2 hover:text-hi"
          >
            <X size={15} />
          </button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto p-2">
          {SHOWN_NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  cn(
                    'flex items-start gap-2.5 rounded-md px-2.5 py-2 transition-colors',
                    isActive
                      ? 'bg-accent-weak text-accent'
                      : 'text-mid hover:bg-surface-2 hover:text-hi',
                  )
                }
              >
                <Icon size={16} className="mt-0.5 shrink-0" aria-hidden />
                <span className="min-w-0">
                  <span className="block text-[12.5px] font-medium">{item.label}</span>
                  <span className="block text-[11px] leading-snug text-lo">
                    {item.description}
                  </span>
                </span>
              </NavLink>
            );
          })}
        </div>

        <div className="shrink-0 border-t border-border p-3">
          <Badge tone="muted" size="xs">
            Tap <span className="num">g</span> then a letter on a keyboard
          </Badge>
        </div>
      </aside>
    </div>
  );
}
